import {
  CANONICAL_ALLERGEN_CODES,
  FOOD_DIETARY_SUITABILITY_OPTIONS,
  USER_DIETARY_PREFERENCE_OPTIONS,
} from "./constants";

const CANONICAL_ALLERGEN_CODE_SET = new Set(CANONICAL_ALLERGEN_CODES);
const FOOD_SUITABILITY_OPTION_SET = new Set(FOOD_DIETARY_SUITABILITY_OPTIONS);
const USER_PREFERENCE_OPTION_SET = new Set(USER_DIETARY_PREFERENCE_OPTIONS);

function getBoolean(value, fallback = false) {
  return typeof value === "boolean" ? value : fallback;
}

function unique(values) {
  return Array.from(new Set(values));
}

export function isCanonicalAllergenCode(value) {
  return CANONICAL_ALLERGEN_CODE_SET.has(String(value ?? "").trim().toLowerCase());
}

export function normalizeAllergenCode(value) {
  const normalizedValue = String(value ?? "").trim().toLowerCase();
  return isCanonicalAllergenCode(normalizedValue) ? normalizedValue : null;
}

export function normalizeAllergenCodes(values) {
  const normalizedCodes = unique(
    (Array.isArray(values) ? values : [])
      .map(normalizeAllergenCode)
      .filter(Boolean),
  );

  return CANONICAL_ALLERGEN_CODES.filter((code) => normalizedCodes.includes(code));
}

export function normalizeFoodClassification(source) {
  const resolvedSource = source && typeof source === "object" ? source : {};
  const allergenCodes = normalizeAllergenCodes(
    resolvedSource.allergen_codes ?? resolvedSource.allergens,
  );
  const isVegan = getBoolean(resolvedSource.is_vegan ?? resolvedSource.isVegan, false);
  const isVegetarian = getBoolean(
    resolvedSource.is_vegetarian ?? resolvedSource.isVegetarian,
    isVegan,
  );

  return {
    allergen_codes: allergenCodes,
    allergen_data_complete: getBoolean(
      resolvedSource.allergen_data_complete ?? resolvedSource.allergenDataComplete,
      false,
    ),
    is_vegetarian: isVegetarian || isVegan,
    is_vegan: isVegan,
    dietary_data_complete: getBoolean(
      resolvedSource.dietary_data_complete ?? resolvedSource.dietaryDataComplete,
      false,
    ),
  };
}

export function getRuntimeFoodClassification(source) {
  const normalized = normalizeFoodClassification(source);

  return {
    allergens: normalized.allergen_codes,
    allergenDataComplete: normalized.allergen_data_complete,
    isVegetarian: normalized.is_vegetarian,
    isVegan: normalized.is_vegan,
    dietaryDataComplete: normalized.dietary_data_complete,
  };
}

export function getFoodSuitabilityOption(source) {
  const classification = normalizeFoodClassification(source);

  if (!classification.dietary_data_complete) {
    return "unknown";
  }

  if (classification.is_vegan) {
    return "vegan";
  }

  if (classification.is_vegetarian) {
    return "vegetarian";
  }

  return "none";
}

export function getFoodClassificationFromSuitabilityOption(option) {
  const normalizedOption = String(option ?? "").trim().toLowerCase();

  if (!FOOD_SUITABILITY_OPTION_SET.has(normalizedOption)) {
    return {
      is_vegetarian: false,
      is_vegan: false,
      dietary_data_complete: false,
    };
  }

  if (normalizedOption === "vegan") {
    return {
      is_vegetarian: true,
      is_vegan: true,
      dietary_data_complete: true,
    };
  }

  if (normalizedOption === "vegetarian") {
    return {
      is_vegetarian: true,
      is_vegan: false,
      dietary_data_complete: true,
    };
  }

  if (normalizedOption === "none") {
    return {
      is_vegetarian: false,
      is_vegan: false,
      dietary_data_complete: true,
    };
  }

  return {
    is_vegetarian: false,
    is_vegan: false,
    dietary_data_complete: false,
  };
}

export function normalizeUserDietaryPreference(value) {
  const normalizedValue = String(value ?? "none").trim().toLowerCase();
  return USER_PREFERENCE_OPTION_SET.has(normalizedValue)
    ? normalizedValue
    : "none";
}

function createEmptyDerivedClassification() {
  return {
    allergen_codes: [],
    allergen_data_complete: false,
    is_vegetarian: false,
    is_vegan: false,
    dietary_data_complete: false,
  };
}

function createCollectedFoodClassificationState() {
  return {
    hasEntries: false,
    allergenCodes: new Set(),
    allergenDataComplete: true,
    dietaryDataComplete: true,
    isVegetarian: true,
    isVegan: true,
  };
}

function collectFoodClassificationState(source, state = createCollectedFoodClassificationState()) {
  if (!source) {
    return state;
  }

  if (Array.isArray(source)) {
    source.forEach((entry) => collectFoodClassificationState(entry, state));
    return state;
  }

  if (typeof source !== "object") {
    return state;
  }

  let traversedContainer = false;

  if (Array.isArray(source.meals)) {
    source.meals.forEach((meal) => collectFoodClassificationState(meal, state));
    traversedContainer = true;
  }

  if (Array.isArray(source.options)) {
    source.options.forEach((option) => collectFoodClassificationState(option, state));
    traversedContainer = true;
  }

  if (Array.isArray(source.items)) {
    source.items.forEach((item) => collectFoodClassificationState(item, state));
    traversedContainer = true;
  }

  if (Array.isArray(source.ingredients)) {
    source.ingredients.forEach((ingredient) =>
      collectFoodClassificationState(ingredient, state),
    );
    traversedContainer = true;
  }

  if (traversedContainer) {
    return state;
  }

  const classification = normalizeFoodClassification(source);
  state.hasEntries = true;
  classification.allergen_codes.forEach((code) => state.allergenCodes.add(code));
  state.allergenDataComplete =
    state.allergenDataComplete && classification.allergen_data_complete === true;
  state.dietaryDataComplete =
    state.dietaryDataComplete && classification.dietary_data_complete === true;
  state.isVegetarian = state.isVegetarian && classification.is_vegetarian === true;
  state.isVegan = state.isVegan && classification.is_vegan === true;

  if (Array.isArray(source.equivalences)) {
    source.equivalences.forEach((equivalence) =>
      collectFoodClassificationState(equivalence, state),
    );
  }

  return state;
}

export function deriveConservativeFoodClassification(source, overrides = {}) {
  const collectedState = collectFoodClassificationState(source);

  if (!collectedState.hasEntries) {
    return createEmptyDerivedClassification();
  }

  const overrideAdditions = normalizeAllergenCodes(
    overrides.allergen_override_additions ?? overrides.allergenOverrideAdditions,
  );
  const overrideRemovals = normalizeAllergenCodes(
    overrides.allergen_override_removals ?? overrides.allergenOverrideRemovals,
  );
  const finalAllergenCodes = CANONICAL_ALLERGEN_CODES.filter(
    (code) =>
      (collectedState.allergenCodes.has(code) || overrideAdditions.includes(code)) &&
      !overrideRemovals.includes(code),
  );

  return {
    allergen_codes: finalAllergenCodes,
    allergen_data_complete: collectedState.allergenDataComplete,
    is_vegetarian: collectedState.isVegetarian,
    is_vegan: collectedState.isVegan,
    dietary_data_complete: collectedState.dietaryDataComplete,
  };
}

export function deriveRecipeClassification(ingredients, overrides = {}) {
  return deriveConservativeFoodClassification(ingredients, overrides);
}

export {
  CANONICAL_ALLERGEN_CODES,
  FOOD_DIETARY_SUITABILITY_OPTIONS,
  USER_DIETARY_PREFERENCE_OPTIONS,
};
